'use client';

import Chart from '@/components/room/charts/Chart';
import { drawToolFreq, type CurveTone, type ModeTick } from './toolFreqDraw';

export { curveStroke } from './toolFreqDraw';
export type { CurveTone, ModeTick } from './toolFreqDraw';

interface Curve {
  /** Linear magnitude, or dB if `unit` is `'db'`. */
  mag: ArrayLike<number>;
  tone: CurveTone;
  /** Not read by the drawer (there is no in-canvas legend) — callers pass it
   *  as documentation of which curve is which; kept so it isn't a breaking
   *  removal for the one place (blog/_template) that has always used it. */
  label?: string;
  unit?: 'linear' | 'db';
}

interface Props {
  freqs: number[];
  curves: Curve[];
  /** Optional marker frequency (Hz) with label. */
  markerHz?: number;
  markerLabel?: string;
  /** Vertical mode lines (e.g. room resonances). */
  modeTicks?: ModeTick[];
  fMin?: number;
  fMax?: number;
  /**
   * When curves are linear: mean-center each curve in dB (pure stays ~0).
   * Ignored when a curve already has `unit: 'db'`.
   */
  relative?: boolean;
  /** Fixed vertical window (dB). Default auto. */
  dbRange?: { min: number; max: number };
}

/**
 * Log-frequency magnitude chart for the public tools — hosted by the room
 * dock's own `Chart.tsx`, so it gets the site's one crosshair cursor, touch
 * ownership and theme repaint for free (AGENTS.md's chart/cursor rules).
 * `.guided-theme` scopes the `--ed-*` tokens `Chart.tsx` paints with — the
 * SAME `--rt-*` values these tools already use elsewhere on the page
 * (`--ed-bg` and `--app-bg` both alias `--rt-page`, etc.), see
 * globals.css's `.guided-theme` block — a plain tools page mounts no other
 * ancestor that defines them.
 */
export default function ToolFreqChart({
  freqs,
  curves,
  markerHz,
  markerLabel,
  modeTicks,
  fMin = 20,
  fMax = 300,
  relative = false,
  dbRange,
}: Props) {
  return (
    <div className="guided-theme">
      <Chart
        title="Frequency response"
        hideHeader
        testId="tool-freq-chart"
        draw={
          freqs.length
            ? (canvas) => drawToolFreq(canvas, { freqs, curves, markerHz, markerLabel, modeTicks, fMin, fMax, relative, dbRange })
            : null
        }
      />
    </div>
  );
}
